/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useState } from 'react';
import { CreatorProfileDetail, fetchCreatorById } from '../../api';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';

interface CreatorProfileModalProps {
  creatorProfileId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreatorProfileModal: React.FC<CreatorProfileModalProps> = ({
  creatorProfileId,
  open,
  onOpenChange,
}) => {
  const [profile, setProfile] = useState<CreatorProfileDetail | null>(null);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    const load = async () => {
      try {
        const detail = await fetchCreatorById(creatorProfileId);
        if (isMounted) {
          setProfile(detail);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load creator profile', error);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [creatorProfileId, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogTitle>{profile?.displayName ?? 'Creator Profile'}</DialogTitle>
        <ScrollArea className="max-h-[70vh]">
          {profile ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <img
                  src={resolveProfileImage(profile)}
                  alt={profile.displayName}
                  className="h-40 w-full sm:w-40 rounded-xl object-cover border"
                />
                <div>
                  <h3 className="text-xl font-semibold">{profile.displayName}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{profile.platformUri}</p>
                  <div className="flex flex-wrap gap-3">
                    {profile.websiteUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={profile.websiteUrl} target="_blank" rel="noreferrer">
                          Visit Website
                        </a>
                      </Button>
                    )}
                    {profile.platformUri && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={profile.platformUri} target="_blank" rel="noreferrer">
                          Follow Channel
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {profile.bio && (
                <p className="text-sm whitespace-pre-line leading-relaxed">{profile.bio}</p>
              )}
              {profile.featuredLinks && profile.featuredLinks.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Featured links</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.featuredLinks.map((link) => (
                      <Button key={link.url} variant="secondary" size="sm" asChild>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.label}
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CreatorProfileModal;

const resolveProfileImage = (profile: CreatorProfileDetail) => {
  const placeholder = '/logo192.png';
  const hero = profile.heroImageUrl?.trim();
  const avatar = profile.avatarUrl?.trim();

  if (hero && hero.toLowerCase() !== 'string') {
    return hero;
  }

  if (avatar && avatar.toLowerCase() !== 'string') {
    return avatar;
  }

  return placeholder;
};
